/**
 * ChecklistDetailSheet — Navigable side sheet for checklist item details
 *
 * Provides prev/next navigation through filtered items, keyboard shortcuts
 * (arrow keys, j/k), animated slide transitions, and a redesigned header
 * with progress bar, quick stats, and inline complete/uncomplete action.
 */

import { useMemo, useState, useCallback, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, Clock } from "lucide-react";
import { useQuery } from "convex/react";
import { api } from "convex/_generated/api";
import { SideSheet, ConfirmDialog } from "@/components/ui";
import { useChecklistActions } from "@/hooks/use-checklist-actions";
import { useDocumentById } from "@/hooks/use-documents";
import { useApiMutation } from "@/packages/features-shared";
import { useTranslation } from "@/packages/utils";
import { DocumentDetailSheet } from "@/components/documents/DocumentDetailSheet";
import { ChecklistDetailSections, type TabId } from "./ChecklistDetailSections";
import { SheetHeader, type NavFilter } from "./ChecklistDetailSheetHeader";
import { DestructiveConfirmSheet } from "./ChecklistConfirmDialog";
import { ChecklistEditSheet } from "./ChecklistEditSheet";
import type { UIChecklistItem, Id, DocumentId } from "@/packages/types";

function getUnansweredQuestionCount(item: UIChecklistItem): number {
  const questions = item.questions ?? [];
  const answeredOrders = new Set(
    (item.answers ?? []).map((a) => a.question.order),
  );
  return questions.filter((q) => !answeredOrders.has(q.order)).length;
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface ChecklistDetailSheetProps {
  item: UIChecklistItem | null;
  open: boolean;
  onClose: () => void;
  filteredItems: UIChecklistItem[];
  onNavigate: (itemId: string) => void;
  auditId: Id<"audits">;
}

// ─── Nested Document Sheet ────────────────────────────────────────────────────

function NestedDocumentSheet({
  documentId,
  onClose,
}: {
  documentId: DocumentId | null;
  onClose: () => void;
}) {
  const { document } = useDocumentById(documentId as Id<"documents"> | null);

  return (
    <DocumentDetailSheet
      open={documentId !== null}
      onClose={onClose}
      document={document ?? null}
    />
  );
}

// ─── Empty Filter State ───────────────────────────────────────────────────────

function EmptyFilterState({ filter }: { filter: NavFilter }) {
  const { t } = useTranslation("audits");
  const isAllCompleted = filter === "open";

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className="flex flex-col items-center justify-center py-24 px-8 text-center"
    >
      <motion.div
        initial={{ scale: 0, rotate: -15 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ delay: 0.1, type: "spring", stiffness: 220, damping: 16 }}
        className={`size-16 rounded-full flex items-center justify-center mb-4 ${
          isAllCompleted ? "bg-green-500/10" : "bg-border/40"
        }`}
      >
        {isAllCompleted ? (
          <CheckCircle2 size={30} className="text-green-500" />
        ) : (
          <Clock size={30} className="text-muted" />
        )}
      </motion.div>
      <motion.h3
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="text-[15px] font-semibold text-primary mb-1.5"
      >
        {isAllCompleted
          ? (t("checklist.emptyFilter.allCompleted.title") as string)
          : (t("checklist.emptyFilter.noneCompleted.title") as string)}
      </motion.h3>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.28 }}
        className="text-[13px] text-muted max-w-[260px] leading-relaxed"
      >
        {isAllCompleted
          ? (t("checklist.emptyFilter.allCompleted.body") as string)
          : (t("checklist.emptyFilter.noneCompleted.body") as string)}
      </motion.p>
    </motion.div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function ChecklistDetailSheet({
  item,
  open,
  onClose,
  filteredItems,
  onNavigate,
  auditId,
}: ChecklistDetailSheetProps) {
  const { t } = useTranslation("audits");
  const [direction, setDirection] = useState<1 | -1>(1);
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [confirmAction, setConfirmAction] = useState<
    "remove_from_audit" | null
  >(null);
  const [selectedDocumentId, setSelectedDocumentId] =
    useState<DocumentId | null>(null);
  const [editSheetOpen, setEditSheetOpen] = useState(false);
  const [showCompleteGuard, setShowCompleteGuard] = useState(false);
  const [navFilter, setNavFilter] = useState<NavFilter>("all");

  const removeFromAudit = useApiMutation(
    api.checklist_mutations.removeChecklistItem,
    {
      name: "remove-checklist-item",
      successMessage: "Item removed from audit",
    },
  );

  const actions = useChecklistActions(auditId);
  const findings = useQuery(
    api.finding_queries.getByChecklistItem,
    item ? { checklistItemId: item.id as Id<"checklistItems"> } : "skip",
  );
  const findingsCount = findings?.length ?? 0;

  const navigationItems = useMemo(() => {
    if (navFilter === "open")
      return filteredItems.filter((i) => i.status !== "APPROVED");
    if (navFilter === "completed")
      return filteredItems.filter((i) => i.status === "APPROVED");
    return filteredItems;
  }, [filteredItems, navFilter]);

  const handleDestructiveAction = useCallback(() => {
    setConfirmAction("remove_from_audit");
  }, []);

  const handleCloseConfirm = useCallback(() => {
    setConfirmAction(null);
  }, []);

  const handleConfirmRemove = useCallback(async () => {
    if (!item) return;
    await removeFromAudit.mutateAsync({
      checklistItemId: item.id as Id<"checklistItems">,
    });
    setConfirmAction(null);
    onClose();
  }, [item, removeFromAudit, onClose]);

  const currentIndex = useMemo(() => {
    if (!item) return -1;
    return navigationItems.findIndex((i) => i.id === item.id);
  }, [item, navigationItems]);

  const navigatePrev = useCallback(() => {
    if (navigationItems.length === 0) return;
    setDirection(-1);
    const prevIndex =
      currentIndex <= 0 ? navigationItems.length - 1 : currentIndex - 1;
    onNavigate(navigationItems[prevIndex].id);
  }, [navigationItems, currentIndex, onNavigate]);

  const navigateNext = useCallback(() => {
    if (navigationItems.length === 0) return;
    setDirection(1);
    const nextIndex =
      currentIndex >= navigationItems.length - 1 ? 0 : currentIndex + 1;
    onNavigate(navigationItems[nextIndex].id);
  }, [navigationItems, currentIndex, onNavigate]);

  // Keyboard navigation: arrows, j/k
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;

      if (e.key === "ArrowLeft" || e.key === "k") {
        e.preventDefault();
        navigatePrev();
      } else if (e.key === "ArrowRight" || e.key === "j") {
        e.preventDefault();
        navigateNext();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, navigatePrev, navigateNext]);

  const handleComplete = useCallback(() => {
    if (!item) return;
    if (getUnansweredQuestionCount(item) > 0) {
      setShowCompleteGuard(true);
      return;
    }
    actions.complete(item.id as Id<"checklistItems">);
  }, [item, actions]);

  const handleConfirmComplete = useCallback(() => {
    if (!item) return;
    actions.complete(item.id as Id<"checklistItems">);
    setShowCompleteGuard(false);
  }, [item, actions]);

  const handleUncomplete = useCallback(() => {
    if (!item) return;
    actions.uncomplete(item.id as Id<"checklistItems">);
  }, [item, actions]);

  const handleGenerateSummary = useCallback(() => {
    if (!item) return;
    actions.generateSummary(item.id as Id<"checklistItems">);
  }, [item, actions]);

  const handleGenerateInformation = useCallback(
    (instructions: string) => {
      if (!item) return;
      actions.generateInformation(
        item.id as Id<"checklistItems">,
        instructions,
      );
    },
    [item, actions],
  );

  const handleGenerateExamples = useCallback(
    (instructions: string) => {
      if (!item) return;
      actions.generateExamples(item.id as Id<"checklistItems">, instructions);
    },
    [item, actions],
  );

  const handleFindRelatedDocuments = useCallback(() => {
    if (!item) return;
    actions.findRelatedDocuments(item.id as Id<"checklistItems">);
  }, [item, actions]);

  const handleOpenDocument = useCallback((docId: DocumentId) => {
    setSelectedDocumentId(docId);
  }, []);

  const handleCloseDocument = useCallback(() => {
    setSelectedDocumentId(null);
  }, []);

  const handleJumpToTab = useCallback((tab: string) => {
    setActiveTab(tab as TabId);
  }, []);

  const slideVariants = {
    enter: (dir: number) => ({ x: dir > 0 ? 40 : -40, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir: number) => ({ x: dir > 0 ? -40 : 40, opacity: 0 }),
  };

  return (
    <>
      <SideSheet open={open} onClose={onClose} className="w-[840px]!">
        {item && (
          <>
            <SheetHeader
              item={item}
              currentIndex={currentIndex >= 0 ? currentIndex : 0}
              totalCount={navigationItems.length}
              onPrev={navigatePrev}
              onNext={navigateNext}
              onEdit={() => setEditSheetOpen(true)}
              navFilter={navFilter}
              onNavFilterChange={setNavFilter}
              onComplete={handleComplete}
              onUncomplete={handleUncomplete}
              onRemoveFromAudit={handleDestructiveAction}
              findingsCount={findingsCount}
              onJumpToTab={handleJumpToTab}
            />

            <SideSheet.Body>
              {navigationItems.length === 0 ? (
                <EmptyFilterState filter={navFilter} />
              ) : (
                <AnimatePresence mode="wait" custom={direction}>
                  <motion.div
                    key={item.id}
                    custom={direction}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.15, ease: "easeOut" }}
                  >
                    <ChecklistDetailSections
                      item={item}
                      auditId={auditId}
                      activeTab={activeTab}
                      onTabChange={setActiveTab}
                      onGenerateSummary={handleGenerateSummary}
                      onGenerateInformation={handleGenerateInformation}
                      onGenerateExamples={handleGenerateExamples}
                      onFindRelatedDocuments={handleFindRelatedDocuments}
                      onOpenDocument={handleOpenDocument}
                      findingsCount={findingsCount}
                    />
                  </motion.div>
                </AnimatePresence>
              )}
            </SideSheet.Body>
          </>
        )}
      </SideSheet>

      <DestructiveConfirmSheet
        open={confirmAction !== null}
        onClose={handleCloseConfirm}
        action={confirmAction}
        itemTitle={item?.name ?? ""}
        onConfirm={handleConfirmRemove}
      />

      {/* Nested document detail sheet */}
      <NestedDocumentSheet
        documentId={selectedDocumentId}
        onClose={handleCloseDocument}
      />

      {/* Edit checklist item side sheet */}
      <ChecklistEditSheet
        open={editSheetOpen}
        onClose={() => setEditSheetOpen(false)}
        item={item}
        auditId={auditId as Id<"audits">}
      />

      {/* Completion guard — shown when questions are unanswered */}
      <ConfirmDialog
        open={showCompleteGuard}
        onClose={() => setShowCompleteGuard(false)}
      >
        <ConfirmDialog.Title>
          {t("checklist.completionGuard.title")}
        </ConfirmDialog.Title>
        <ConfirmDialog.Body>
          {t("checklist.completionGuard.bodyUnansweredQuestions", {
            count: item ? getUnansweredQuestionCount(item) : 0,
          })}
        </ConfirmDialog.Body>
        <ConfirmDialog.Actions
          confirmLabel={t("checklist.completionGuard.confirm")}
          cancelLabel={t("checklist.completionGuard.cancel")}
          onConfirm={handleConfirmComplete}
          onCancel={() => setShowCompleteGuard(false)}
        />
      </ConfirmDialog>
    </>
  );
}
